import { trackLogfrogEvent } from "./logfrog";

const REDDIT_PIXEL_ID = "a2_j2k567xc77kr";

const META_EVENT_BY_PRODUCT_EVENT = new Map<string, string>([
  ["guest_signup_started", "Lead"],
  ["register_completed", "CompleteRegistration"],
  ["paywall_checkout_started", "InitiateCheckout"],
  ["subscription_activated", "Subscribe"],
]);

const ACQUISITION_EVENTS = new Set([
  "guest_deals_viewed",
  "guest_retailer_selected",
  "guest_deal_opened",
  "guest_outbound_clicked",
  "deal_share_opened",
  "guest_signup_prompt_viewed",
  "guest_signup_started",
  "guest_signin_started",
	"guest_local_preview_started",
	"guest_local_preview_results",
	"guest_local_preview_failed",
	"guest_pricing_viewed",
  "guest_prompt_dismissed",
  "guest_intent_resumed",
]);

const AUTH_EVENTS = new Set(["login_completed", "register_completed", "signup"]);
const LANDING_EVENTS = new Set([
  "utm_captured",
  "landing_page_viewed",
  "landing_cta_clicked",
]);

// Existing signed-in product and commerce events are intentionally catalogued
// here instead of accepting arbitrary event names/properties. This preserves
// the funnels the app already relies on while keeping guest and account data
// out of analytics payloads by default.
const PRODUCT_EVENT_PROPERTY_KEYS = new Map<string, ReadonlySet<string>>([
  ["setup_step_viewed", new Set(["step", "step_name", "is_reinstating", "has_walmart", "has_home_depot"])],
  ["setup_step_error", new Set(["step", "step_name"])],
  ["setup_step_completed", new Set(["from_step", "to_step", "step_name", "next_step_name", "retailers", "has_walmart", "has_home_depot", "is_reinstating"])],
  ["setup_completed", new Set(["step", "step_name", "retailers", "has_walmart", "has_home_depot", "walmart_stores", "hd_stores", "is_reinstating"])],
  ["setup_completed_no_retailers", new Set(["step", "step_name"])],
  ["setup_step_back", new Set(["from_step", "to_step", "step_name", "previous_step_name"])],
	["paywall_viewed", new Set(["source"])],
  ["paywall_plan_selected", new Set(["plan_id", "plan_name", "price"])],
  ["paywall_checkout_started", new Set(["plan_id", "walmart_stores", "hd_stores"])],
  ["paywall_checkout_error", new Set(["plan_id"])],
  ["payment-attempted", new Set()],
  ["payment-successful", new Set(["eventType"])],
  ["payment-failed", new Set()],
  ["landing_products_menu_clicked", new Set(["feature"])],
  ["navigation-clicked", new Set(["tab", "tabLabel", "navigationType", "fromPath", "action", "isLinked"])],
  ["penny_meals_promo_viewed", new Set(["placement", "isMember"])],
  ["penny_meals_promo_tapped", new Set(["placement", "isMember", "fromPath"])],
  ["penny_meals_promo_dismissed", new Set(["placement", "isMember", "fromPath"])],
  ["theme-toggled", new Set(["newTheme"])],
  ["language-toggled", new Set(["from", "to", "source"])],
  ["auth_email_continue", new Set(["exists"])],
  ["auth_oauth_clicked", new Set(["method"])],
  ["upgrade-modal-plan-selected", new Set(["planId", "planName"])],
  ["upgrade-modal-checkout-error", new Set(["planId"])],
  ["sidebar-collapse-toggled", new Set(["collapsed"])],
  ["ibotta_offer_viewed", new Set(["surface", "offerCount", "bestScore"])],
  ["resale_viewed", new Set(["retailer", "outcome", "amazon_status", "ebay_status"])],
  ["resale_sales_opened", new Set(["retailer", "count"])],
  ["resale_link_clicked", new Set(["retailer", "market"])],
  ["onboarding_viewed", new Set()],
  ["onboarding_intent_selected", new Set(["intent"])],
  ["onboarding_zip_saved", new Set()],
  ["onboarding_deal_mode_selected", new Set(["mode", "tier"])],
  ["onboarding_step_viewed", new Set(["step"])],
  ["onboarding_not_covered_viewed", new Set(["has_nearest", "nearest_miles"])],
  ["onboarding_not_covered_action", new Set(["action"])],
  ["onboarding_coverage_notify", new Set(["status"])],
  ["app_store_badge_clicked", new Set(["platform", "location"])],
  ["landing_fresh_finds_viewed", new Set(["surface", "source", "retailer", "deal_count"])],
  ["landing_fresh_find_clicked", new Set(["surface", "source", "retailer", "position", "product_key"])],
  ["verification-attempted", new Set()],
  ["verification-successful", new Set()],
  ["verification-error", new Set()],
  ["feature_page_viewed", new Set(["feature"])],
  ["feature_cta_clicked", new Set(["feature", "location"])],
  ["feature_cross_link_clicked", new Set(["from", "to"])],
  ["product_recommendations_viewed", new Set(["surface", "source_retailer", "result_count"])],
  ["product_recommendation_clicked", new Set(["surface", "source_retailer", "retailer", "match_type", "rank"])],
  ["subscription_activated", new Set()],
	["cancellation_flow_opened", new Set(["plan_id", "billing_provider"])],
	["cancellation_reason_selected", new Set(["reason_category", "plan_id"])],
	["retention_offer_viewed", new Set(["reason_category", "offer_type", "plan_id", "target_plan"])],
	["retention_offer_accepted", new Set(["reason_category", "offer_type", "plan_id", "target_plan"])],
	["retention_offer_skipped", new Set(["reason_category", "offer_type", "plan_id", "target_plan"])],
	["retention_offer_error", new Set(["reason_category", "offer_type", "plan_id", "target_plan"])],
	["cancellation_submitted", new Set(["reason_category", "plan_id", "billing_provider"])],
	["cancellation_succeeded", new Set(["reason_category", "plan_id", "billing_provider"])],
	["cancellation_error", new Set(["reason_category", "plan_id", "billing_provider"])],
	["subscription_reactivated", new Set(["plan_id", "billing_provider"])],
]);

const ACQUISITION_PROPERTY_KEYS = new Set(["surface", "retailer", "trigger", "source"]);
// Outcome measurement for the public ZIP radar: how many scans return deals,
// why scans fail, and whether sign-ups from the results panel saw deals.
const ACQUISITION_EXTRA_PROPERTY_KEYS = new Map<string, ReadonlySet<string>>([
	["guest_local_preview_results", new Set(["deal_count", "stores_scanned", "has_deals"])],
	["guest_local_preview_failed", new Set(["reason"])],
	["guest_signup_started", new Set(["deal_count", "has_deals"])],
]);
const AUTH_PROPERTY_KEYS = new Set(["method", "source"]);
const UTM_PROPERTY_KEYS = new Set(["utm_source", "utm_medium", "utm_campaign"]);
const LANDING_CTA_PROPERTY_KEYS = new Set(["location", "plan_name"]);

function getAllowedPropertyKeys(event: string) {
  if (ACQUISITION_EVENTS.has(event)) {
    const extra = ACQUISITION_EXTRA_PROPERTY_KEYS.get(event);
    return extra ? new Set([...ACQUISITION_PROPERTY_KEYS, ...extra]) : ACQUISITION_PROPERTY_KEYS;
  }
  if (AUTH_EVENTS.has(event)) return AUTH_PROPERTY_KEYS;
  if (event === "utm_captured") return UTM_PROPERTY_KEYS;
  if (event === "landing_page_viewed") {
    return new Set([...UTM_PROPERTY_KEYS, "referrer"]);
  }
  if (event === "landing_cta_clicked") return LANDING_CTA_PROPERTY_KEYS;
  if (PRODUCT_EVENT_PROPERTY_KEYS.has(event)) {
    return PRODUCT_EVENT_PROPERTY_KEYS.get(event)!;
  }
  return new Set<string>();
}

function sanitizeAnalyticsProperties(
  event: string,
  properties?: Record<string, unknown>
) {
  if (!properties) return undefined;
  const allowedKeys = getAllowedPropertyKeys(event);
  const safe: Record<string, string | number | boolean | null> = {};
  for (const [key, value] of Object.entries(properties)) {
    if (!allowedKeys.has(key)) continue;
    if (key === "referrer" && typeof value === "string") {
      try {
        const hostname = new URL(value).hostname.toLowerCase();
        if (hostname) safe.referrer_hostname = hostname.slice(0, 253);
      } catch {
        /* Invalid or relative referrers are omitted. */
      }
    } else if (typeof value === "string") safe[key] = value.slice(0, 256);
    else if (typeof value === "number" && Number.isFinite(value)) safe[key] = value;
    else if (typeof value === "boolean" || value === null) safe[key] = value;
  }
  return Object.keys(safe).length ? safe : undefined;
}

export function track(event: string, properties?: Record<string, unknown>) {
  const normalizedEvent = event.trim().slice(0, 96);
  if (
    !/^[a-z][a-z0-9_-]*$/.test(normalizedEvent) ||
    (!ACQUISITION_EVENTS.has(normalizedEvent) &&
      !AUTH_EVENTS.has(normalizedEvent) &&
      !LANDING_EVENTS.has(normalizedEvent) &&
      !PRODUCT_EVENT_PROPERTY_KEYS.has(normalizedEvent))
  ) {
    // Unlisted events are dropped silently in production; make that loud
    // while developing so a new track() call is not lost unnoticed.
    if (import.meta.env.DEV) {
      console.warn(`[analytics] dropped event "${event}": not in the analytics.ts allowlist`);
    }
    return;
  }
  trackLogfrogEvent(
    normalizedEvent,
    sanitizeAnalyticsProperties(normalizedEvent, properties)
  );
  const metaEvent = META_EVENT_BY_PRODUCT_EVENT.get(normalizedEvent);
  if (metaEvent && typeof window.fbq === "function") {
    // Deliberately omit event properties so account, store, and product data
    // cannot cross this advertising boundary.
    window.fbq("track", metaEvent);
  }
}

export function trackMetaPageView() {
  if (typeof window.fbq !== "function") return;
  window.fbq("track", "PageView");
}

export function identify(userId: string, traits?: Record<string, string>) {
  // Identity is handled by the privacy-aware Logfrog integration; keep the
  // legacy analytics API callable without forwarding account traits here.
  void userId;
  void traits;
}

export function identifyReddit(match: { email?: string; phoneNumber?: string; externalId?: string }) {
  if (typeof window.rdt !== "function") return;
  const keys: NonNullable<Parameters<NonNullable<Window["rdt"]>>[2]> = {};
  if (match.email) keys.email = match.email;
  if (match.phoneNumber) keys.phoneNumber = match.phoneNumber;
  if (match.externalId) keys.externalId = match.externalId;
  if (!Object.keys(keys).length) return;
  window.rdt("init", REDDIT_PIXEL_ID, keys);
}

export function resetAnalytics() {}

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign"] as const;

const GOOGLE_ADS_PURCHASE_CONVERSION = "AW-17789498497/U79nCPSXyLMcEIHp2KJC";

export function trackGoogleAdsPurchaseConversion(transactionId = "") {
  if (typeof window.gtag !== "function") return;
  window.gtag("event", "conversion", {
    send_to: GOOGLE_ADS_PURCHASE_CONVERSION,
    value: 1.0,
    currency: "USD",
    transaction_id: transactionId,
  });
}

export function captureUtm() {
  const params = new URLSearchParams(window.location.search);
  const utm: Record<string, string> = {};
  for (const key of UTM_KEYS) {
    const val = params.get(key);
    if (val) utm[key] = val;
  }
  if (Object.keys(utm).length) track("utm_captured", utm);
  return utm;
}
